/**
 * @file GuestTeasers.jsx
 * @description Teaser screens shown to guest users for locked features (Best Matches,
 *   My Applications). Each screen provides a visual preview of the feature and a
 *   sign-up gate prompting account creation.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module components/GuestTeasers
 */

import { useTranslation } from "react-i18next";

const D = {
  pageBg:    "#F4F4F2",
  cardBg:    "#FFFFFF",
  navy:      "#12103A",
  navyMid:   "#1E1B4B",
  navyLight: "#EEECF8",
  textBody:  "#3D3A52",
  textMuted: "#7C798F",
  border:    "#E6E5E2",
  green:     "#0A6B4E",
  greenBg:   "#F0FBF5",
  greenLight:"#D1FAE5",
  amber:     "#92400E",
  amberBg:   "#FFF8F0",
};

// ── Shared gate CTA ──────────────────────────────────────────────────────────
function Gate({ onLoginRequired }) {
  const { t } = useTranslation();
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      gap: 14, padding: "32px 28px",
      background: "#fff",
      border: `1.5px solid ${D.navyMid}22`,
      borderRadius: 18,
      boxShadow: "0 4px 24px rgba(18,16,58,0.08)",
      maxWidth: 420, width: "100%",
    }}>
      {/* Lock icon */}
      <div style={{
        width: 52, height: 52, borderRadius: "50%",
        background: D.navyLight,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={D.navyMid} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      </div>

      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: D.navy, marginBottom: 6 }}>
          {t('guestTeasers.gateTitle')}
        </div>
        <div style={{ fontSize: 13, color: D.textBody, lineHeight: 1.6 }}>
          {t('guestTeasers.gateDesc')}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
        <button
          onClick={() => onLoginRequired?.("signup")}
          style={{
            background: D.navyMid, color: "#fff", border: "none",
            borderRadius: 10, padding: "12px 0", fontSize: 14,
            fontWeight: 700, cursor: "pointer", width: "100%",
            fontFamily: "'Inter', 'DM Sans', sans-serif",
            transition: "background 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = D.navy; }}
          onMouseLeave={e => { e.currentTarget.style.background = D.navyMid; }}
        >
          {t('guestTeasers.buttonSignup')}
        </button>
        <button
          onClick={() => onLoginRequired?.("login")}
          style={{
            background: "transparent", color: D.textMuted, border: `1px solid ${D.border}`,
            borderRadius: 10, padding: "10px 0", fontSize: 13,
            fontWeight: 500, cursor: "pointer", width: "100%",
            fontFamily: "'Inter', 'DM Sans', sans-serif",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = D.navyMid + "66"; e.currentTarget.style.color = D.navy; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = D.border; e.currentTarget.style.color = D.textMuted; }}
        >
          {t('guestTeasers.buttonLogin')}
        </button>
      </div>
    </div>
  );
}

// ── Blurred mock match card — mirrors the real dual-score design ──────────────
// hire chance (primary, "your odds") + skill fit (secondary, "how well you match")
function MockMatchCard({ hireChance, skillFit, title, pay, tags, blur }) {
  return (
    <div style={{
      background: D.cardBg, border: `1px solid ${D.border}`,
      borderRadius: 14, padding: "16px 18px",
      filter: blur ? "blur(4px)" : "none",
      opacity: blur ? 0.55 : 1,
      pointerEvents: "none", userSelect: "none",
      position: "relative",
    }}>
      {/* Score row — hire chance (primary) + skill fit (secondary) */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 7,
            background: D.greenBg, border: `1px solid ${D.greenLight}`,
            borderRadius: 8, padding: "4px 10px",
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill={D.green} stroke="none">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            <span style={{ fontSize: 12, fontWeight: 800, color: D.green }}>{hireChance}% hire chance</span>
          </div>
          <span style={{
            fontSize: 11, fontWeight: 700, color: D.navyMid,
            background: D.navyLight, borderRadius: 7, padding: "4px 9px",
          }}>{skillFit}% skill fit</span>
        </div>
        <span style={{ fontSize: 12, color: D.textMuted }}>{pay}</span>
      </div>

      <div style={{ fontSize: 14, fontWeight: 700, color: D.navy, marginBottom: 8 }}>{title}</div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {tags.map(t => (
          <span key={t} style={{
            fontSize: 11, fontWeight: 600,
            background: D.navyLight, color: D.navyMid,
            borderRadius: 6, padding: "3px 8px",
          }}>{t}</span>
        ))}
      </div>
    </div>
  );
}

// ── Best Matches teaser ──────────────────────────────────────────────────────
export function BestMatchesTeaser({ onLoginRequired }) {
  const { t } = useTranslation();

  const mockCards = [
    { hireChance: 92, skillFit: 96, title: "ABA Therapist, South Pasadena, CA", pay: "$28–32/hr", tags: ["DTT", "Early Intervention", "PECS"], blur: false },
    { hireChance: 84, skillFit: 88, title: "RBT Needed, San Marino, CA", pay: "$26–30/hr", tags: ["NET", "AAC", "Challenging Behavior"], blur: true },
    { hireChance: 71, skillFit: 81, title: "Behavior Technician, Alhambra, CA", pay: "$24–28/hr", tags: ["Social Skills", "VB", "Telehealth"], blur: true },
  ];

  // "What you're missing" — skills that would unlock more cases nearby.
  // Mirrors the real "Skills that would unlock more cases" panel in Best Matches.
  const unlockSkills = [
    { skill: "VB-MAPP",   cases: 8 },
    { skill: "Telehealth", cases: 5 },
    { skill: "Feeding",   cases: 3 },
  ];

  return (
    <div style={{
      flex: 1, overflowY: "auto",
      background: D.pageBg,
      fontFamily: "'Inter', 'DM Sans', sans-serif",
    }}>
      <div style={{
        maxWidth: 720, margin: "0 auto", padding: "40px 24px 60px",
      }}>
        {/* Header */}
        <div style={{ marginBottom: 32, textAlign: "center" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: D.greenBg, border: `1px solid ${D.greenLight}`,
            borderRadius: 20, padding: "5px 14px", marginBottom: 16,
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill={D.green} stroke="none">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            <span style={{ fontSize: 12, fontWeight: 700, color: D.green }}>{t('guestTeasers.bestMatchesHeader')}</span>
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: D.navy, marginBottom: 10 }}>
            {t('guestTeasers.bestMatchesTitle')}
          </div>
          <div style={{ fontSize: 14, color: D.textBody, lineHeight: 1.7, maxWidth: 500, margin: "0 auto" }}>
            {t('guestTeasers.bestMatchesDesc')}
          </div>
        </div>

        {/* Feature highlights */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 32,
        }}>
          {[
            { icon: "M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z", label: "Location fit", desc: "Cases near your zip code" },
            { icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2", label: "Skill matching", desc: "Ranked by your certifications" },
            { icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z", label: "Pay range", desc: "Filtered to your target rate" },
          ].map(h => (
            <div key={h.label} style={{
              background: "#fff", border: `1px solid ${D.border}`,
              borderRadius: 12, padding: "16px 14px", textAlign: "center",
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: D.navyLight, display: "flex",
                alignItems: "center", justifyContent: "center",
                margin: "0 auto 10px",
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={D.navyMid} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d={h.icon}/>
                </svg>
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: D.navy, marginBottom: 3 }}>{h.label}</div>
              <div style={{ fontSize: 11, color: D.textMuted }}>{h.desc}</div>
            </div>
          ))}
        </div>

        {/* Mock match cards preview */}
        <div style={{ position: "relative", marginBottom: 32 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {mockCards.map((c, i) => <MockMatchCard key={i} {...c} />)}
          </div>

          {/* Fade-out overlay over blurred cards */}
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0, height: 200,
            background: "linear-gradient(to bottom, transparent, " + D.pageBg + ")",
            pointerEvents: "none",
          }} />
        </div>

        {/* What you're missing — skills that would unlock more cases */}
        <div style={{
          background: "#fff", border: `1px solid ${D.border}`,
          borderRadius: 14, padding: "20px 22px", marginBottom: 32,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={D.amber} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 2" />
            </svg>
            <span style={{ fontSize: 15, fontWeight: 800, color: D.navy }}>{t('guestTeasers.bestMatchesUnlockTitle')}</span>
          </div>
          <div style={{ fontSize: 13, color: D.textBody, lineHeight: 1.6, marginBottom: 14 }}>
            {t('guestTeasers.bestMatchesUnlockDesc')}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {unlockSkills.map(u => (
              <div key={u.skill} style={{
                display: "flex", alignItems: "center", gap: 8,
                background: D.amberBg, border: `1px solid ${D.amber}33`,
                borderRadius: 10, padding: "8px 13px",
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: D.navy }}>+ {u.skill}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: D.amber }}>unlocks {u.cases} cases</span>
              </div>
            ))}
          </div>
        </div>

        {/* Gate */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Gate onLoginRequired={onLoginRequired} />
        </div>
      </div>
    </div>
  );
}

// ── Mock application row ─────────────────────────────────────────────────────
function MockAppRow({ title, agency, stage, blur }) {
  const stageColors = {
    Applied:   { bg: D.navyLight,  color: D.navyMid },
    Reviewing: { bg: D.amberBg,    color: D.amber    },
    Interview: { bg: "#FEF3C7",    color: "#92400E"  },
    Offer:     { bg: D.greenBg,    color: D.green    },
  };
  const sc = stageColors[stage] || stageColors.Applied;

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 14,
      background: "#fff", border: `1px solid ${D.border}`,
      borderRadius: 12, padding: "14px 16px",
      filter: blur ? "blur(4px)" : "none",
      opacity: blur ? 0.5 : 1,
      pointerEvents: "none", userSelect: "none",
    }}>
      {/* Status dot */}
      <div style={{
        width: 8, height: 8, borderRadius: "50%",
        background: sc.color, flexShrink: 0,
      }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: D.navy, marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: 11, color: D.textMuted }}>{agency}</div>
      </div>

      <div style={{
        fontSize: 11, fontWeight: 700,
        background: sc.bg, color: sc.color,
        borderRadius: 7, padding: "4px 10px",
        flexShrink: 0,
      }}>
        {stage}
      </div>

      {/* Arrow */}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={D.textMuted} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12"/>
        <polyline points="12 5 19 12 12 19"/>
      </svg>
    </div>
  );
}

// ── My Applications teaser ───────────────────────────────────────────────────
export function MyApplicationsTeaser({ onLoginRequired }) {
  const { t } = useTranslation();

  const mockApps = [
    { title: "ABA Therapist, Pasadena, CA",       agency: t("trackerShowcase.sampleAgency", "Sample Agency"),    stage: "Offer",     blur: false },
    { title: "Behavior Technician, Arcadia, CA",  agency: "Sample Agency", stage: "Interview", blur: true  },
    { title: "RBT Position, Monrovia, CA",        agency: "Pacific ABA Group",  stage: "Reviewing", blur: true  },
    { title: "ABA Therapist, Temple City, CA",    agency: "Allied Behavior",    stage: "Applied",   blur: true  },
  ];

  const stages = ["Applied", "Reviewing", "Interview", "Offer"];

  return (
    <div style={{
      flex: 1, overflowY: "auto",
      background: D.pageBg,
      fontFamily: "'Inter', 'DM Sans', sans-serif",
    }}>
      <div style={{ maxWidth: 660, margin: "0 auto", padding: "40px 24px 60px" }}>

        {/* Header */}
        <div style={{ marginBottom: 32, textAlign: "center" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: D.amberBg, border: `1px solid ${D.amber}33`,
            borderRadius: 20, padding: "5px 14px", marginBottom: 16,
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={D.amber} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
            </svg>
            <span style={{ fontSize: 12, fontWeight: 700, color: D.amber }}>{t('guestTeasers.appsHeader')}</span>
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: D.navy, marginBottom: 10 }}>
            {t('guestTeasers.appsTitle')}
          </div>
          <div style={{ fontSize: 14, color: D.textBody, lineHeight: 1.7, maxWidth: 460, margin: "0 auto" }}>
            {t('guestTeasers.appsDesc')}
          </div>
        </div>

        {/* Pipeline stages */}
        <div style={{ marginBottom: 28 }}>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 0, marginBottom: 24,
          }}>
            {stages.map((s, i) => (
              <div key={s} style={{ display: "flex", alignItems: "center" }}>
                <div style={{
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                  padding: "0 8px",
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: "50%",
                    background: i === 3 ? D.greenBg : (i === 2 ? "#FEF3C7" : D.navyLight),
                    border: `2px solid ${i === 3 ? D.green : (i === 2 ? "#92400E" : D.navyMid)}44`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 700,
                    color: i === 3 ? D.green : (i === 2 ? "#92400E" : D.navyMid),
                  }}>
                    {i + 1}
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 600, color: D.textMuted, whiteSpace: "nowrap" }}>{s}</span>
                </div>
                {i < stages.length - 1 && (
                  <div style={{ width: 32, height: 1.5, background: D.border, marginBottom: 16 }} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Mock application rows */}
        <div style={{ position: "relative", marginBottom: 32 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {mockApps.map((a, i) => <MockAppRow key={i} {...a} />)}
          </div>

          {/* Fade overlay */}
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0, height: 180,
            background: "linear-gradient(to bottom, transparent, " + D.pageBg + ")",
            pointerEvents: "none",
          }} />
        </div>

        {/* Feature bullets */}
        <div style={{
          display: "flex", flexDirection: "column", gap: 8, marginBottom: 32,
          background: "#fff", border: `1px solid ${D.border}`,
          borderRadius: 14, padding: "18px 20px",
        }}>
          {[
            "See real-time status updates as agencies review your application",
            "Message agencies directly through the platform",
            "Get notified the moment an offer arrives",
            "Keep notes on each case you applied to",
          ].map(text => (
            <div key={text} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <div style={{
                width: 18, height: 18, borderRadius: "50%",
                background: D.greenBg, display: "flex", alignItems: "center",
                justifyContent: "center", flexShrink: 0, marginTop: 1,
              }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={D.green} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <span style={{ fontSize: 13, color: D.textBody, lineHeight: 1.5 }}>{text}</span>
            </div>
          ))}
        </div>

        {/* Gate */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Gate onLoginRequired={onLoginRequired} />
        </div>
      </div>
    </div>
  );
}
